import { useEffect, useRef, useState } from "react";
import { claimMarqueeImages } from "@/lib/claim-marquee-images";

/**
 * Bilderlauf im Claim-Abschnitt der Startseite (Mobil/Tablet und Desktop).
 *
 * Safari-fest umgesetzt:
 * - Die Laufstrecke wird als gemessene Pixelbreite gesetzt (`--marquee-distance`),
 *   nicht als `-50%`. Prozentwerte werden bei jeder Layoutänderung neu
 *   ausgewertet; wenn Bilder nachträglich laden, springt oder verschwindet
 *   die Bahn dadurch in WebKit.
 * - Die Dauer folgt aus der Messung, damit die Geschwindigkeit auf jedem
 *   Viewport gleich viele Pixel pro Sekunde beträgt.
 * - Die Bilder laden sofort (kein `lazy`) und haben eine Platzhalter-Ratio,
 *   damit die Bahnbreite von Anfang an stabil ist.
 */

const MASKS = {
  mobile:
    "linear-gradient(to right, transparent 0%, transparent 5%, black 15%, black 100%)",
  desktop:
    "linear-gradient(to right, transparent 0%, transparent 10%, black 25%, black 100%)",
} as const;

type Variant = "mobile" | "desktop";

interface ClaimMarqueeProps {
  variant: Variant;
  /** Laufgeschwindigkeit in Pixeln pro Sekunde. */
  pxPerSecond?: number;
  className?: string;
}

export function ClaimMarquee({
  variant,
  pxPerSecond = variant === "mobile" ? 90 : 70,
  className,
}: ClaimMarqueeProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [metrics, setMetrics] = useState<{ distance: number; duration: number } | null>(
    null,
  );

  const measureRef = useRef<() => void>(() => {});

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const measure = () => {
      const half = el.scrollWidth / 2;
      if (half < 100) return;
      setMetrics((prev) => {
        const distance = Math.round(half);
        if (prev && Math.abs(prev.distance - distance) < 2) return prev;
        return {
          distance,
          duration: Math.round((distance / pxPerSecond) * 10) / 10,
        };
      });
    };

    measureRef.current = measure;
    measure();

    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    window.addEventListener("load", measure);

    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
      window.removeEventListener("load", measure);
    };
  }, [pxPerSecond]);

  if (claimMarqueeImages.length === 0) {
    if (variant === "mobile") return null;
    return (
      <div
        aria-hidden="true"
        className="h-[320px] rounded-2xl border border-dashed border-border/60"
      />
    );
  }

  const loop = [...claimMarqueeImages, ...claimMarqueeImages];

  return (
    <div
      aria-hidden="true"
      className={`relative overflow-hidden ${className ?? ""}`}
      style={{
        WebkitMaskImage: MASKS[variant],
        maskImage: MASKS[variant],
      }}
    >
      <div
        ref={trackRef}
        className={`marquee-track-claim flex h-full w-max items-center ${
          variant === "mobile" ? "gap-4" : "gap-8"
        }`}
        style={
          metrics
            ? ({
                "--marquee-distance": `${metrics.distance}px`,
                "--marquee-duration": `${metrics.duration}s`,
              } as React.CSSProperties)
            : undefined
        }
      >
        {loop.map((img, i) => (
          <img
            key={`claim-${variant}-${img.src}-${i}`}
            src={img.src}
            alt={img.alt}
            draggable={false}
            decoding="async"
            onLoad={(event) => {
              // Platzhalter-Ratio entfernen, sobald die echten Maße bekannt
              // sind, und die Bahn neu messen (Safari-Sprünge vermeiden).
              event.currentTarget.style.aspectRatio = "";
              measureRef.current();
            }}
            className="h-full w-auto shrink-0 rounded-[4px] object-cover shadow-elegant"
            style={{ aspectRatio: "3 / 2" }}
          />
        ))}
      </div>
    </div>
  );
}
